import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { motion } from "framer-motion";
import { Car } from "lucide-react";

export default function Cars() {
  const [dark, setDark] = useState(true);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState({});

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const sync = () =>
      setDark(localStorage.getItem("theme") !== "light");
    sync();
    window.addEventListener("theme-change", sync);
    return () => window.removeEventListener("theme-change", sync);
  }, []);

  /* ================= LOAD CARS ================= */
  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(collection(db, "cars"));
      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // default first image
      const defaults = {};
      data.forEach((c) => {
        defaults[c.id] = c.images?.[0] || c.image;
      });

      setActiveImage(defaults);
      setCars(data);
      setLoading(false);
    };

    load();
  }, []);

  const theme = {
    page: dark ? "bg-[#0b0b0b] text-white" : "bg-gray-100 text-gray-900",
    card: dark
      ? "bg-white/5 border-white/10"
      : "bg-white border-gray-200",
    muted: dark ? "text-gray-400" : "text-gray-600",
  };

  /* ================= UI ================= */
  return (
    <div className={`min-h-screen px-6 py-20 ${theme.page}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto"
      >
        {/* HEADER */}
        <div className="text-center mb-14">
          <h1 className="text-4xl font-bold flex justify-center items-center gap-3">
            <Car size={32} /> Our Hearse Fleet
          </h1>
          <p className={`mt-3 ${theme.muted}`}>
            Dignified, well-maintained vehicles for every service
          </p>
        </div>

        {/* CONTENT */}
        {loading ? (
          <div className="text-center py-20 opacity-60">
            Loading hearse vehicles…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cars.map((car) => (
              <div
                key={car.id}
                className={`rounded-3xl overflow-hidden border ${theme.card}`}
              >
                {/* MAIN IMAGE */}
                <img
                  src={activeImage[car.id]}
                  alt={car.name}
                  className="w-full h-56 object-cover"
                />

                {/* BODY */}
                <div className="p-5">
                  <h3 className="text-lg font-semibold">{car.name}</h3>
                  <p className={`text-sm mt-1 ${theme.muted}`}>
                    Plate: {car.plate}
                  </p>

                  {/* THUMBNAILS */}
                  <div className="flex gap-2 mt-4">
                    {(car.images || [car.image]).map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt=""
                        onClick={() =>
                          setActiveImage((a) => ({
                            ...a,
                            [car.id]: img,
                          }))
                        }
                        className={`w-14 h-14 object-cover rounded-lg cursor-pointer border ${
                          activeImage[car.id] === img
                            ? "border-sky-500"
                            : "border-transparent"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
